import { useTranslation } from "react-i18next";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Method {
  id: string;
  name: string;
  category: string;
  description: string;
  steps: any[];
}

interface MethodsViewProps {
  methods: Method[];
  selectedCategory: string | null;
  onCategorySelect: (category: string | null) => void;
  onMethodClick: (method: Method) => void;
  categories: string[];
}

export const MethodsView = ({
  methods,
  selectedCategory,
  onCategorySelect,
  onMethodClick,
  categories,
}: MethodsViewProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // Show all methods without category filter
  const filteredMethods = methods;

  const getShortCategoryName = (category: string) => {
    if (category.startsWith("Systemische Konzepte - ")) {
      return category.replace("Systemische Konzepte - ", "");
    }
    return category;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Methods List */}
      <div className="flex-1">
        <ScrollArea className={isMobile ? "h-[calc(100vh-12rem)]" : "h-[calc(100vh-12rem)]"}>
          <div className="space-y-2 pr-3">
            {filteredMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => onMethodClick(method)}
                className="group p-3 bg-card rounded-lg border border-border hover:border-primary hover:bg-accent/5 transition-all cursor-pointer hover:shadow-md animate-fade-in"
              >
                <div className="space-y-2">
                  <div className="flex flex-col gap-1.5">
                    <h4 className="font-semibold text-sm leading-tight group-hover:text-primary transition-colors">
                      {method.name}
                    </h4>
                    <Badge variant="outline" className="text-xs w-fit">
                      {getShortCategoryName(method.category)}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                    {method.description}
                  </p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground/70">
                    <span>{method.steps.length} {t('methods.steps')}</span>
                    <span>•</span>
                    <span className="italic">{t('methods.tapForDetails', 'Tippen für Details')}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};
